/**
 * Binary Format Editor Modal
 *
 * Main modal dialog for creating and editing binary format definitions.
 * Includes tabs for basic info, field definitions, and preview.
 */

import React, { useState, useCallback } from 'react';
import { Loader2, Plus, Save, X, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { BinaryFieldEditor } from './BinaryFieldEditor';
import { ByteVisualizer } from './ByteVisualizer';
import { BinaryFormatPreview } from './BinaryFormatPreview';
import { useBinaryFormatStore } from '@/stores/binary-formats/binary-format-store';
import type { InputFormat, Endianness, ValidationMode } from '@/types/binaryFormat';

export const BinaryFormatEditorModal: React.FC = () => {
  const {
    isEditorOpen,
    currentFormat,
    editingFormatId,
    isSaving,
    error,
    closeEditor,
    updateCurrentFormat,
    addField,
    saveCurrentFormat,
    setError,
  } = useBinaryFormatStore();

  const [activeTab, setActiveTab] = useState('basic');
  const [tagInput, setTagInput] = useState('');

  // Handle tag input
  const handleAddTag = useCallback(() => {
    if (tagInput.trim() && currentFormat) {
      const newTags = [...(currentFormat.tags || []), tagInput.trim()];
      updateCurrentFormat({ tags: newTags });
      setTagInput('');
    }
  }, [tagInput, currentFormat, updateCurrentFormat]);

  const handleRemoveTag = useCallback((tagToRemove: string) => {
    if (currentFormat) {
      const newTags = (currentFormat.tags || []).filter((t) => t !== tagToRemove);
      updateCurrentFormat({ tags: newTags });
    }
  }, [currentFormat, updateCurrentFormat]);

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  // Handle save
  const handleSave = async () => {
    setError(null);
    const result = await saveCurrentFormat();
    if (result) {
      // Success - modal closes automatically
    }
  };

  if (!isEditorOpen || !currentFormat) {
    return null;
  }

  return (
    <Dialog open={isEditorOpen} onOpenChange={(open) => !open && closeEditor()}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {editingFormatId ? 'Edit Binary Format' : 'Create Binary Format'}
          </DialogTitle>
        </DialogHeader>

        {/* Error Display */}
        {error && (
          <Alert variant="destructive" className="mx-1">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="fields">
              Fields ({currentFormat.fields?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="preview">Preview</TabsTrigger>
          </TabsList>

          {/* Basic Info Tab */}
          <TabsContent value="basic" className="flex-1 overflow-auto">
            <ScrollArea className="h-[50vh]">
              <div className="space-y-4 p-1">
                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="format-name">Name *</Label>
                  <Input
                    id="format-name"
                    value={currentFormat.name || ''}
                    onChange={(e) => updateCurrentFormat({ name: e.target.value })}
                    placeholder="My Binary Format"
                  />
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label htmlFor="format-description">Description</Label>
                  <Textarea
                    id="format-description"
                    value={currentFormat.description || ''}
                    onChange={(e) => updateCurrentFormat({ description: e.target.value })}
                    placeholder="Describe what this format parses..."
                    rows={3}
                  />
                </div>

                {/* Input Format & Endianness */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="input-format">Input Format *</Label>
                    <Select
                      value={currentFormat.inputFormat || 'base64'}
                      onValueChange={(value) => updateCurrentFormat({ inputFormat: value as InputFormat })}
                    >
                      <SelectTrigger id="input-format">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="base64">Base64</SelectItem>
                        <SelectItem value="hex">Hex String</SelectItem>
                        <SelectItem value="binary">Binary String (0/1)</SelectItem>
                        <SelectItem value="raw">Raw Bytes</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      How the input data is encoded
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="default-endian">Default Byte Order *</Label>
                    <Select
                      value={currentFormat.defaultEndian || 'BE'}
                      onValueChange={(value) => updateCurrentFormat({ defaultEndian: value as Endianness })}
                    >
                      <SelectTrigger id="default-endian">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="BE">Big Endian (Network Order)</SelectItem>
                        <SelectItem value="LE">Little Endian</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Byte order for multi-byte values
                    </p>
                  </div>
                </div>

                {/* Expected Bytes & Validation Mode */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="expected-bytes">Expected Bytes</Label>
                    <Input
                      id="expected-bytes"
                      type="number"
                      min="0"
                      value={currentFormat.expectedBytes || ''}
                      onChange={(e) =>
                        updateCurrentFormat({
                          expectedBytes: e.target.value ? parseInt(e.target.value) : undefined,
                        })
                      }
                      placeholder="Optional"
                    />
                    <p className="text-xs text-muted-foreground">
                      Expected payload size for validation
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="validation-mode">Validation Mode</Label>
                    <Select
                      value={currentFormat.validationMode || 'lenient'}
                      onValueChange={(value) => updateCurrentFormat({ validationMode: value as ValidationMode })}
                    >
                      <SelectTrigger id="validation-mode">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="strict">Strict (fail on any error)</SelectItem>
                        <SelectItem value="lenient">Lenient (continue on errors)</SelectItem>
                        <SelectItem value="silent">Silent (suppress warnings)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      How to handle parsing errors
                    </p>
                  </div>
                </div>

                {/* Tags */}
                <div className="space-y-2">
                  <Label>Tags</Label>
                  <div className="flex gap-2">
                    <Input
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={handleTagKeyDown}
                      placeholder="Add tag..."
                      className="flex-1"
                    />
                    <Button type="button" variant="secondary" onClick={handleAddTag}>
                      Add
                    </Button>
                  </div>
                  {currentFormat.tags && currentFormat.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {currentFormat.tags.map((tag) => (
                        <Badge
                          key={tag}
                          variant="secondary"
                          className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground"
                          onClick={() => handleRemoveTag(tag)}
                        >
                          {tag} <X className="h-3 w-3 ml-1" />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </ScrollArea>
          </TabsContent>

          {/* Fields Tab */}
          <TabsContent value="fields" className="flex-1 overflow-auto">
            <div className="flex flex-col h-[50vh]">
              {/* Byte Visualizer (collapsible) */}
              <ByteVisualizer />

              {/* Field List */}
              <div className="flex-1 min-h-0">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-sm font-medium">Field Definitions</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addField()}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Field
                  </Button>
                </div>
                <ScrollArea className="h-[calc(100%-40px)]">
                  <BinaryFieldEditor />
                </ScrollArea>
              </div>
            </div>
          </TabsContent>

          {/* Preview Tab */}
          <TabsContent value="preview" className="flex-1 overflow-auto">
            <BinaryFormatPreview />
          </TabsContent>
        </Tabs>

        {/* Footer Actions */}
        <div className="flex items-center justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={closeEditor} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Format
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BinaryFormatEditorModal;
